import type React from "react"
import { usePage } from "@inertiajs/react"
import { useLocalePath } from "@/lib/locale-path"
import { bandBg } from "@/components/gisela/TextBand"
import { useMobilePager } from "@/components/MobilePager"
import type { PageProps } from "@/types/inertia"

export interface RelatedArticle {
  title: string
  excerpt: string | null
  path: string
  /** Cover photo (card variant) or a legacy image path; null → emoji. */
  thumbnail_url: string | null
  emoji: string | null
  /** Only ever true for a signed-in admin: a draft shown as a preview. */
  draft?: boolean
}

/** The small marker on a draft — admins only ever see it, so English is fine. */
function DraftTag() {
  return (
    <span className="inline-flex items-center rounded-full border border-signal bg-signal-faded px-2 py-0.5 text-xs font-medium text-ink-body">
      Draft preview
    </span>
  )
}

/**
 * The downward half of the cluster's hub-and-spoke: a condition or therapy
 * page listing the articles written to support it. Articles are filed under
 * a page in the admin (articles.condition_slug / therapy_slug), so these
 * lists maintain themselves — nothing here is hand-curated, and both render
 * nothing until an article points at the page.
 *
 * Two presentations of the same list, chosen by viewport:
 * - `RelatedArticlesSidebar` — from `xl` up, a sticky column in the right
 *   gutter with its own scrollbar: image tiles with the title beneath. The page wraps its bands in
 *   `relative xl:[&>section]:pr-80` so every band leaves the gutter free while
 *   its full-bleed background still spans the viewport.
 * - `RelatedArticlesBand` — below `xl`, a band of cards before the CTA. On a
 *   phone it shows one card at a time — swiped across, or paged by the small
 *   arrows on the card's left and right edges — and ends with a link through
 *   to the blog.
 */

function Thumb({ article, className }: { article: RelatedArticle; className: string }) {
  return (
    <span className={`flex shrink-0 items-center justify-center overflow-hidden bg-stone ${className}`} aria-hidden>
      {article.thumbnail_url ? (
        <img src={article.thumbnail_url} alt="" loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <span className="text-4xl">{article.emoji ?? "📄"}</span>
      )}
    </span>
  )
}

export function RelatedArticlesSidebar({ articles }: { articles?: RelatedArticle[] }) {
  const { props } = usePage<PageProps>()
  const tc = props.translations.gisela_pages.common

  if (!articles || articles.length === 0) return null

  // The aside starts 4rem down (top-16) so the heading lines up with the first
  // band's own heading (bands are py-16), and spans the rest of the band stack
  // (bottom-0) so the sticky column travels with the reader and stops at the
  // end of the page body. Its sticky top clears the fixed nav (h-16) plus the
  // admin bar when one is showing. A hairline on the left sets the column
  // apart; the tiles float on the band background rather than in a card.
  return (
    <aside
      aria-labelledby="related-articles-heading"
      className="absolute top-16 right-0 bottom-0 hidden w-80 xl:block"
    >
      {/* The column is capped at the height of the aside as well as the
          viewport (max-h-full against a box with both top and bottom set), and
          the list takes whatever is left of it. Without that cap a long list
          on a short page grows past the bands it belongs to and lands on top
          of whatever the page closes with — sticky positioning clamps where a
          box sits, never how tall it is. */}
      <div className="sticky top-[calc(var(--admin-bar-h)+5rem)] mr-8 flex max-h-full flex-col border-l border-hairline pl-6">
        <h3 id="related-articles-heading">{tc.related_articles}</h3>
        <ul className="mt-5 max-h-[calc(100vh-var(--admin-bar-h)-10rem)] min-h-0 flex-1 list-none space-y-6 overflow-y-auto pr-4 pb-2 [scrollbar-width:thin] [scrollbar-color:var(--color-hairline)_transparent]">
          {articles.map((article) => (
            <li key={article.path}>
              <a href={article.path} className="group block text-ink-body no-underline">
                <Thumb
                  article={article}
                  className="aspect-[4/3] w-full rounded-xl shadow-sm ring-1 ring-hairline transition-shadow group-hover:shadow-md"
                />
                <span className="mt-2 block text-sm leading-snug group-hover:text-teal-mid">{article.title}</span>
                {article.draft && <span className="mt-1 block"><DraftTag /></span>}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  )
}

export function RelatedArticlesBand({
  articles,
  className = "bg-stone",
}: {
  articles?: RelatedArticle[]
  className?: string
}) {
  const { props } = usePage<PageProps>()
  const tc = props.translations.gisela_pages.common
  const lp = useLocalePath()
  // A card is nearly a phone screen tall, so phones get one at a time — swiped
  // across, or moved with the arrows on the card's own edges, with the count
  // under the heading. From `sm` the grid shows everything. Hooks run before
  // the early return.
  const pager = useMobilePager(articles?.length ?? 0, { pageSize: 1, variant: "compact", edgeArrows: true })

  if (!articles || articles.length === 0) return null

  return (
    <section className={`${className} py-16 xl:hidden`}>
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        <h2>{tc.related_articles}</h2>
        {pager.controls}
        <div className="relative">
          {pager.edgeControls}
          <ul
            ref={pager.listRef as React.RefObject<HTMLUListElement>}
            className="mt-6 grid scroll-mt-24 list-none grid-cols-1 gap-6 p-0 sm:mt-8 sm:grid-cols-2 lg:grid-cols-3"
          >
            {articles.map((article, index) => (
              <li key={article.path} className={`overflow-hidden rounded-2xl bg-white ring-1 ring-hairline ${pager.itemClass(index)}`}>
                {/* An article without a cover photo gets a shallow emoji strip
                    rather than a picture-shaped empty box. */}
                <Thumb
                  article={article}
                  className={article.thumbnail_url ? "aspect-[16/9] w-full sm:aspect-[4/3]" : "h-24 w-full sm:h-28"}
                />
                <div className="p-5">
                  {article.draft && <div className="mb-2"><DraftTag /></div>}
                  <h3>
                    <a href={article.path}>{article.title}</a>
                  </h3>
                  {article.excerpt && <p className="mt-2 text-ink-muted">{article.excerpt}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
        <p className="mt-8">
          <a href={lp("/blog")} className="font-medium text-teal-mid no-underline hover:text-teal hover:underline">
            {tc.more_on_blog}
          </a>
        </p>
      </div>
    </section>
  )
}

/**
 * Background for the CTA that follows the related-articles slot. The band
 * variant only exists below `xl`, so the CTA's place in the cream/stone
 * alternation differs by breakpoint: at `xl` it takes the band's slot, below
 * it the one after.
 */
export function ctaBgAfterRelated(bandCount: number, hasArticles: boolean) {
  if (!hasArticles) return bandBg(bandCount)
  return bandCount % 2 === 0 ? "bg-cream max-xl:bg-stone" : "bg-stone max-xl:bg-cream"
}
